import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ConnectSuccessData,
  WalletConnectCancelled,
} from '@orionprotocol/wallet-api';
import { keyValueStorage, wallets } from '@orionprotocol/common';
import { i18n } from '@lingui/core';
import { useToast } from '@orionprotocol/ui';

import {
  getAppChainId,
  getAvailableNetworks,
  getIsCustomConfig,
  getWalletType,
} from '../../shared/redux/selectors';
import { appSlice, userSlice } from '../../shared/redux/slices';
import { useEnv, useWalletAPI, WalletAPI } from '../../shared/hooks';

export type ConnectEventHandlers = {
  onConnectSuccess?: (data: ConnectSuccessData, isAutoconnect: boolean) => void;
  onConnectError?: (error: unknown) => void;
  onConnectCancelled?: (message?: string) => void;
  onDisconnected?: () => void;
};

function saveAppChainId(newChainId: string): void {
  keyValueStorage.setItem('appChainId', newChainId);
}

export function useWallet(eventHandlers: ConnectEventHandlers): void {
  const walletType = useSelector(getWalletType);
  const walletAPI = useWalletAPI();
  const isCustomConfig = useSelector(getIsCustomConfig);
  const appChainId = useSelector(getAppChainId);
  const { env, envs } = useEnv();
  const availableNetworks = useSelector(getAvailableNetworks);
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const eventHandlersRef = useRef<ConnectEventHandlers>();
  eventHandlersRef.current = eventHandlers;

  const [isAutoconnect, setIsAutoconnect] = useState(true);
  const [activeWalletAPI, setActiveWalletAPI] = useState<WalletAPI>();
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    if (walletType === undefined) {
      setIsAutoconnect(false);
      setIsConnecting(false);
    }
  }, [walletType]);

  // connect
  useEffect(() => {
    if (
      walletType !== undefined &&
      walletAPI &&
      (!activeWalletAPI || activeWalletAPI.type !== walletAPI.type) &&
      !isConnecting
    ) {
      const wallet = wallets.find(({ name }) => name === walletAPI.type);
      if (
        !wallet ||
        (wallet.supportedChains &&
          wallet.supportedChains.includes(appChainId) === false)
      ) {
        dispatch(userSlice.actions.disconnectWallet());
        return;
      }

      const chainInfo = availableNetworks[appChainId];

      if (chainInfo) {
        setIsConnecting(true);

        walletAPI
          .connect({
            chainInfo,
            isAutoconnect,
          })
          .then(async (data: ConnectSuccessData) => {
            setIsConnecting(false);
            const { address, chainId } = data;
            setActiveWalletAPI(walletAPI);
            const walletConnectWalletName =
              await walletAPI.getWalletConnectWalletName?.();

            dispatch(
              userSlice.actions.setWallet({
                address,
                chainId,
                walletType: walletAPI.type,
                walletConnectWalletName,
              }),
            );
            eventHandlersRef.current?.onConnectSuccess?.(data, isAutoconnect);
          })
          .catch((e) => {
            dispatch(userSlice.actions.disconnectWallet());

            if (e instanceof WalletConnectCancelled) {
              eventHandlersRef.current?.onConnectCancelled?.();
            } else {
              console.error(e);
              const errorMessage =
                e instanceof Error
                  ? wallet?.getErrorMessage?.(e) ?? e.message
                  : undefined;
              showToast({
                // iconType: 'error',
                text:
                  errorMessage !== undefined
                    ? errorMessage
                    : i18n._(
                        'Can not connect to wallet. Try to reload the page',
                      ),
              });
              eventHandlersRef.current?.onConnectError?.(e);
            }
          })
          .finally(() => {
            setIsConnecting(false);
            setIsAutoconnect(false);
          });
      }
    }
  }, [
    activeWalletAPI,
    appChainId,
    availableNetworks,
    dispatch,
    isConnecting,
    isAutoconnect,
    walletAPI,
    walletType,
    showToast,
  ]);

  // disconnect
  useEffect(() => {
    if (
      walletType === undefined ||
      (activeWalletAPI && activeWalletAPI.type !== walletType)
    ) {
      if (activeWalletAPI) activeWalletAPI?.disconnect?.();
      setActiveWalletAPI(undefined);
    }
  }, [activeWalletAPI, dispatch, walletType]);

  useEffect(() => {
    if (isCustomConfig !== undefined) {
      saveAppChainId(appChainId);
    }
  }, [appChainId, isCustomConfig]);

  useEffect(() => {
    if (
      isCustomConfig !== undefined &&
      env !== undefined &&
      !availableNetworks?.[appChainId]
    ) {
      dispatch(userSlice.actions.disconnectWallet());
      const firstAvailableNetwork = Object.values(availableNetworks)[0].chainId;
      const envInfo = envs[env];
      if (envInfo) {
        dispatch(
          appSlice.actions.setAppNetwork(
            envInfo.defaultNetwork ?? firstAvailableNetwork,
          ),
        );
      }
    }
  }, [availableNetworks, dispatch, env, envs, appChainId, isCustomConfig]);
}
